/**
 * Same algorithm as osint-users/utils/canonicalFieldKey.js.
 * Singular/plural and case-only pairs are the SAME key.
 */

import { isActionableEmailValue } from '../services/link-analysis/link-analysis-searchable.util';

const EMPTY_STRINGS = new Set(['', 'n/a', 'na', 'null', 'undefined', 'unknown']);

const KNOWN_SIMPLE_PLURALS = new Set([
  'username',
  'name',
  'email',
  'phone',
  'tag',
  'vpa',
  'image',
  'email_hint',
]);

const DISPLAY_LABEL_OVERRIDES: Record<string, string> = {
  id: 'ID',
  profile_url: 'Profile URL',
  vpa: 'VPA',
  name: 'Name',
  username: 'Username',
  email: 'Email',
  email_hint: 'Email hint',
  phone: 'Phone',
  tag: 'Tag',
  image: 'Image',
  hours_timezone: 'Timezone',
};

const IDENTITY_ORDER = [
  'name',
  'email',
  'username',
  'id',
  'profile_url',
  // Imo (and similar): keep social stats above structured lists like voice_clubs
  'follower_count',
  'following_count',
  'is_following',
];

/**
 * Grouped section cards (DishTV): fixed display order. Stored JSONB reorders keys,
 * so the API key order cannot be relied on.
 */
const GROUPED_SECTION_ORDER = ['subscriber', 'address', 'account', 'loyalty', 'package'];

function hasGroupedSubscriberSection(fields: unknown): boolean {
  if (!isPlainObject(fields)) return false;
  const raw: any = (fields as Record<string, unknown>)['subscriber'];
  const value = isPlainObject(raw) && 'value' in raw ? raw.value : raw;
  const row = Array.isArray(value) ? value[0] : value;
  return isPlainObject(row);
}

export function canonicalFieldKey(raw: unknown): string {
  let k = String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  if (k === 'handle') k = 'username';
  if (k === 'uid' || k === 'telegram_id') k = 'id';
  if (k === 'url' || k === 'maps_url') k = 'profile_url';
  if (k === 'display_name') k = 'name';
  if (k.endsWith('s') && k.length > 2) {
    const singular = k.slice(0, -1);
    if (KNOWN_SIMPLE_PLURALS.has(singular)) k = singular;
  }
  return k;
}

function humanizeRawKey(raw: unknown): string {
  if (!raw || typeof raw !== 'string') return '';
  return raw
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ');
}

export function displayLabelForFieldKey(raw: unknown, originalRaw?: unknown): string {
  const key = canonicalFieldKey(raw);
  if (!key) return 'Unknown';
  if (DISPLAY_LABEL_OVERRIDES[key]) return DISPLAY_LABEL_OVERRIDES[key];
  return humanizeRawKey(originalRaw ?? raw) || humanizeRawKey(key) || 'Unknown';
}

function isPlainObject(value: unknown): boolean {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isContractField(value: unknown): value is { value: unknown } {
  return isPlainObject(value) && Object.prototype.hasOwnProperty.call(value, 'value');
}

export function unwrapFieldValue(value: unknown): unknown {
  if (value === null || value === undefined) return null;
  if (isContractField(value)) return value.value;
  return value;
}

function isPlaceholderString(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();
  if (!trimmed) return true;
  return EMPTY_STRINGS.has(trimmed.toLowerCase());
}

function flattenFieldValues(input: unknown, out: unknown[] = []): unknown[] {
  if (input === null || input === undefined) return out;
  if (isContractField(input)) return flattenFieldValues(input.value, out);
  if (Array.isArray(input)) {
    for (const item of input) flattenFieldValues(item, out);
    return out;
  }
  out.push(input);
  return out;
}

export function uniqueCanonicalValues(input: unknown): unknown[] {
  const seen = new Set<string>();
  const out: unknown[] = [];
  for (const value of flattenFieldValues(input)) {
    if (isPlaceholderString(value)) continue;
    if (value && typeof value === 'object') {
      let sig = 'obj:';
      try {
        sig += JSON.stringify(value);
      } catch {
        sig += String(value);
      }
      if (seen.has(sig)) continue;
      seen.add(sig);
      out.push(value);
      continue;
    }
    const sig = `prim:${String(value).trim().toLowerCase()}`;
    if (!sig.slice(5) || seen.has(sig)) continue;
    seen.add(sig);
    out.push(value);
  }
  return out;
}

export interface MergedPlatformField {
  key: string;
  label: string;
  values: unknown[];
  value: unknown;
  searchType?: string;
  rawField?: any;
  keepEmptyList?: boolean;
}

interface FieldBucket {
  values: unknown[];
  searchType?: string;
  rawField?: any;
  keepEmptyList?: boolean;
  originalKey?: string;
}

function extractSearchType(raw: unknown): string | undefined {
  if (!isPlainObject(raw)) return undefined;
  const record = raw as Record<string, unknown>;
  const typed = record['osint-search-type'] ?? record['osintSearchType'];
  return typeof typed === 'string' && typed.trim() ? typed : undefined;
}

function isEmailFamilyKey(rawKey: unknown): boolean {
  const key = canonicalFieldKey(rawKey);
  if (key === 'email' || key === 'email_hint') return true;
  const raw = String(rawKey || '')
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, '_');
  return (
    raw === 'email_address' ||
    raw === 'email_addresses'
  );
}

/** Masked / incomplete email values are display-only. Real addresses stay searchable. */
export function deniesFieldSearchIcon(detail: any): boolean {
  if (!detail || typeof detail !== 'object') return false;
  const searchType = String(detail['osint-search-type'] || '')
    .trim()
    .toLowerCase();
  const key = canonicalFieldKey(detail.key || detail.label || '');
  if (searchType === 'email_hint' || key === 'email_hint') return true;
  if (searchType && searchType !== 'email') return false;
  if (searchType !== 'email' && key !== 'email') return false;
  const value = detail.value;
  const items = Array.isArray(value) ? value : [value];
  return !items.some((item) => isActionableEmailValue(item));
}

function addPlainBucket(
  buckets: Map<string, FieldBucket>,
  rawKey: unknown,
  rawValue: unknown,
): void {
  const key = canonicalFieldKey(rawKey);
  if (!key) return;
  const unwrapped = unwrapFieldValue(rawValue);
  const emptyList = Array.isArray(unwrapped) && unwrapped.length === 0;
  const values = uniqueCanonicalValues(rawValue);
  const existing = buckets.get(key) || { values: [] };
  if (emptyList) existing.keepEmptyList = true;
  existing.values = uniqueCanonicalValues([...existing.values, ...values]);
  const searchType = extractSearchType(rawValue);
  if (searchType && !existing.searchType) existing.searchType = searchType;
  if (rawValue != null && existing.rawField == null) existing.rawField = rawValue;
  if (!existing.originalKey && rawKey != null && String(rawKey).trim()) {
    existing.originalKey = String(rawKey);
  }
  buckets.set(key, existing);
}

function addToBuckets(
  buckets: Map<string, FieldBucket>,
  rawKey: unknown,
  rawValue: unknown,
): void {
  if (!isEmailFamilyKey(rawKey)) {
    addPlainBucket(buckets, rawKey, rawValue);
    return;
  }

  const values = uniqueCanonicalValues(rawValue);
  const emails: unknown[] = [];
  const hints: unknown[] = [];
  for (const value of values) {
    if (typeof value === 'string' && isActionableEmailValue(value)) emails.push(value);
    else if (value != null && String(value).trim() !== '') hints.push(value);
  }

  if (!emails.length && !hints.length) {
    const unwrapped = unwrapFieldValue(rawValue);
    if (Array.isArray(unwrapped) && unwrapped.length === 0) {
      addPlainBucket(
        buckets,
        canonicalFieldKey(rawKey) === 'email_hint' ? 'email_hint' : 'email',
        rawValue,
      );
    }
    return;
  }

  if (emails.length) {
    addPlainBucket(buckets, 'email', {
      type: emails.length > 1 ? 'list' : 'string',
      value: emails.length === 1 ? emails[0] : emails,
      'osint-search-type': 'email',
    });
  }
  if (hints.length) {
    addPlainBucket(buckets, 'email_hint', {
      type: hints.length > 1 ? 'list' : 'string',
      value: hints.length === 1 ? hints[0] : hints,
      'osint-search-type': 'email_hint',
    });
  }
}

function addAccountToBuckets(buckets: Map<string, FieldBucket>, account: any): void {
  if (!isPlainObject(account)) return;
  addToBuckets(buckets, 'name', account.name ?? account.display_name);
  addToBuckets(buckets, 'username', account.username ?? account.handle);
  addToBuckets(buckets, 'id', account.uid ?? account.id ?? account.telegram_id);
  addToBuckets(
    buckets,
    'profile_url',
    account.profile_url ?? account.profileUrl ?? account.url ?? account.maps_url,
  );
  const reserved = new Set([
    'name',
    'display_name',
    'username',
    'handle',
    'uid',
    'id',
    'telegram_id',
    'url',
    'profile_url',
    'profileurl',
    'maps_url',
  ]);
  for (const [rawKey, rawValue] of Object.entries(account)) {
    if (reserved.has(canonicalFieldKey(rawKey)) || reserved.has(String(rawKey).toLowerCase())) {
      continue;
    }
    addToBuckets(buckets, rawKey, rawValue);
  }
}

function addInlineAccountToBuckets(
  buckets: Map<string, FieldBucket>,
  platformData: any,
): void {
  if (!isPlainObject(platformData)) return;
  addToBuckets(buckets, 'name', platformData.name ?? platformData.names);
  addToBuckets(buckets, 'username', platformData.username ?? platformData.vpas);
  addToBuckets(buckets, 'id', platformData.uid ?? platformData.id);
  addToBuckets(
    buckets,
    'profile_url',
    platformData.profile_url ?? platformData.maps_url,
  );
}

/**
 * Merge accounts[] + fields (+ inline identity) into unique canonical keys.
 * Never keeps both username and usernames; never keeps N/A when a real alias exists.
 *
 * When fields.subscribers is a structured object list (Tata Play), skip accounts[]
 * so the UI does not also show parallel NAME / BALANCE / TOKEN columns.
 */
export function collectMergedPlatformDisplayFields(platformData: any = {}): MergedPlatformField[] {
  const buckets = new Map<string, FieldBucket>();
  const accounts = Array.isArray(platformData?.accounts) ? platformData.accounts : [];
  const subscribersField = platformData?.fields?.subscribers;
  const subscribersValue =
    subscribersField &&
    typeof subscribersField === 'object' &&
    !Array.isArray(subscribersField) &&
    'value' in subscribersField
      ? (subscribersField as { value?: unknown }).value
      : subscribersField;
  const hasStructuredSubscribers =
    Array.isArray(subscribersValue) && subscribersValue.length > 0;

  if (!hasStructuredSubscribers) {
    if (accounts.length > 0) {
      for (const account of accounts) addAccountToBuckets(buckets, account);
    } else if (isPlainObject(platformData?.account)) {
      addAccountToBuckets(buckets, platformData.account);
    } else {
      addInlineAccountToBuckets(buckets, platformData);
    }
  }

  const fields = platformData?.fields;
  if (isPlainObject(fields)) {
    for (const [rawKey, rawValue] of Object.entries(fields)) {
      if (String(rawKey).toLowerCase() === 'registered') continue;
      // Flat keys already represented inside each subscribers[] row
      if (
        hasStructuredSubscribers &&
        [
          'name',
          'names',
          'status',
          'balance',
          'amount',
          'balance_token',
          'balance_tokens',
          'subscriber_id',
          'subscriber_ids',
          'subscriber_names',
          'subscriber_statuses',
          'subscriber_details',
          'balance_end_date',
          'end_date',
        ].includes(canonicalFieldKey(rawKey))
      ) {
        continue;
      }
      addToBuckets(buckets, rawKey, rawValue);
    }
  }

  let orderedKeys = [
    ...IDENTITY_ORDER.filter((key) => buckets.has(key)),
    ...[...buckets.keys()].filter((key) => !IDENTITY_ORDER.includes(key)),
  ];
  if (hasGroupedSubscriberSection(fields)) {
    orderedKeys = [
      ...GROUPED_SECTION_ORDER.filter((key) => orderedKeys.includes(key)),
      ...orderedKeys.filter((key) => !GROUPED_SECTION_ORDER.includes(key)),
    ];
  }

  const rows: MergedPlatformField[] = [];
  for (const key of orderedKeys) {
    const bucket = buckets.get(key);
    if (!bucket) continue;
    if (!bucket.values.length && !bucket.keepEmptyList) continue;
    const value =
      bucket.values.length === 0
        ? []
        : bucket.values.length === 1
          ? bucket.values[0]
          : bucket.values;
    rows.push({
      key,
      label: displayLabelForFieldKey(key, bucket.originalKey),
      values: bucket.values,
      value,
      searchType: bucket.searchType,
      rawField: bucket.rawField,
      keepEmptyList: bucket.keepEmptyList,
    });
  }
  return rows;
}

const LEFTOVER_NOT_FOUND_FIELD_KEYS = new Set([
  'phone',
  'mobile',
  'message',
  'error',
  'query',
]);

function fieldValueIsPresent(field: unknown): boolean {
  if (field == null) return false;
  if (
    typeof field === 'object' &&
    !Array.isArray(field) &&
    field !== null &&
    'value' in field
  ) {
    const value = (field as { value?: unknown }).value;
    return value != null && value !== '';
  }
  return field !== '';
}

/** Query-echo / vendor-error only — must not count as a found account. */
export function isLeftoverNotFoundSource(platformData: unknown): boolean {
  if (!platformData || typeof platformData !== 'object' || Array.isArray(platformData)) {
    return false;
  }
  const source = platformData as {
    accounts?: unknown;
    fields?: Record<string, unknown>;
  };
  const accounts = Array.isArray(source.accounts) ? source.accounts : [];
  if (
    accounts.some(
      (row) => row && typeof row === 'object' && Object.keys(row as object).length,
    )
  ) {
    return false;
  }
  const fields = source.fields;
  if (!fields || typeof fields !== 'object' || Array.isArray(fields)) return false;
  const keys = Object.keys(fields).filter((key) => fieldValueIsPresent(fields[key]));
  if (!keys.length) return false;
  return keys.every((key) => LEFTOVER_NOT_FOUND_FIELD_KEYS.has(canonicalFieldKey(key)));
}
